/**
 * rPI Porter — Auto-reply rule React Query hooks (E-102 P3-1)
 *
 * staleTime = 60 s: auto-reply rules change rarely (owner-managed).
 * After create/update/delete, invalidate the list cache.
 */

import {
  createAutoReplyRule,
  deleteAutoReplyRule,
  listAutoReplyRules,
  updateAutoReplyRule,
  type CreateAutoReplyInput,
  type UpdateAutoReplyInput,
} from '@/api/porter-auto-reply';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const AUTO_REPLY_STALE_MS = 60_000;
const QUERY_KEY = ['porter', 'auto-reply'] as const;

export function useAutoReplyRules() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: listAutoReplyRules,
    staleTime: AUTO_REPLY_STALE_MS,
  });
}

export function useCreateAutoReplyRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAutoReplyInput) => createAutoReplyRule(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useUpdateAutoReplyRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateAutoReplyInput }) =>
      updateAutoReplyRule(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteAutoReplyRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAutoReplyRule(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
